<template>
  <div class="comment-actions">
    <span
      :class="{ 'branding-active-text': showReply }"
      v-if="!comment.parentId && !comment.deleted"
      tabindex="0"
      aria-label="Reply to comment"
      role="button"
      @click="toggleReply"
      @keyup.enter="toggleReply"
      >Reply</span
    >
    <span
      class="replies-button"
      v-if="comment.replies && comment.replies.length > 0"
      :class="{ 'branding-active-text': showReplies }"
      @click="$emit('setShowReplies', { commentId: comment.id })"
      @keyup.enter="$emit('setShowReplies', { commentId: comment.id })"
      tabindex="0"
      role="button"
      :aria-label="(showReplies ? 'Hide ' : 'Show ') + (comment.replies.length > 1 ? 'Replies' : 'Reply')"
    >
      {{ showReplies ? 'Hide' : 'Show' }} {{ comment.replies.length > 1 ? 'replies' : 'reply' }}
    </span>
    <span
      :class="{ 'branding-active-text': showEdit }"
      v-if="comment.self && !comment.deleted"
      tabindex="0"
      aria-label="Edit Comment"
      role="button"
      @click="toggleEdit"
      @keyup.enter="toggleEdit"
      >Edit</span
    >
    <span
      v-if="
        (comment.self ||
          (user.userType === 'teacher' && !user.isStudentOwnerLMBPreview) ||
          (user.userType === 'student' && user.isTeacherLmbAttempt)) &&
        !comment.deleted
      "
      tabindex="0"
      aria-label="Delete Comment"
      role="button"
      @click="deleteComment"
      @keyup.enter="deleteComment"
    >
      Delete
    </span>
    <discussion-input
      v-if="renderReply && !comment.deleted"
      v-show="showReply"
      :interactionId="interactionId"
      mode="reply"
      placeholder="reply to this comment"
      :commentId="comment.id"
      v-on:toggleReply="toggleReply"
      v-on:setShowReplies="setShowReplies"
      :showReplies="showReplies"
      ariaLabel="Input field for discussion reply text."
    ></discussion-input>
    <discussion-input
      v-if="renderEdit && comment.self && !comment.deleted"
      v-show="showEdit"
      :interactionId="interactionId"
      mode="edit"
      placeholder="edit this comment"
      :commentId="comment.id"
      :startingText="comment.text"
      :visible="renderEdit && showEdit"
      v-on:toggleEdit="toggleEdit"
      ref="editInput"
      ariaLabel="Input field for editting discussion reply text."
    ></discussion-input>
  </div>
</template>

<script>
// Components:
import DiscussionInput from '@/components/interaction/discussion/DiscussionInput';

// Services:
import EventBus from '@/buses/EventBus';

// Plugins / Libraries:
import {
  mapActions,
  mapGetters,
  mapState
} from 'vuex';
import _ from 'lodash';

// Global Mixin(s):
import Modal from '@mixins/Modal/Modal.js';

export default {
  name: 'discussion-actions',
  components: {
    DiscussionInput,
  },
  props: [
    'comment',
    'discussionPreview',
    'interactionId',
    'showReplies',
  ],
  mixins: [ Modal ],
  data() {
    return {
      renderReply: false,
      showReply: false,
      renderEdit: false,
      showEdit: false,
      isDeletingComment: false,
    };
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
    ]),
    ...mapGetters('player', [
      'bulbAttempt',
      'user',
    ]),
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
  },
  methods: {
    ...mapActions('interactions', [
      'deleteDiscussionComment',
      'deleteDummyDiscussionComment',
      'loadDiscussionComments',
    ]),
    deleteComment() {
      if (
        this.isDeletingComment
        || (
          this.isLiveSession
          && this.interactionMeta.pastSessionTime
        )
      ) return;
      if (this.discussionPreview) {
        this.deleteDummyComment(this.comment.id);
        return;
      }
      this.openModal({
        applicationContext: 'discussionActions',
        title: `<div class="player-discussion-comment-deletion-modal-html-title-container"><h2>Warning!</h2></div>`,
        icon: 'warning',
        iconColor: '#FFFFFF',
        text: 'Are you sure you want to delete this comment from the discussion forum?',
        confirmButtonText: 'Delete',
        focusConfirm: false,
        showCloseButton: false,
        customClass: {
          container: 'player-discussion-comment-deletion-modal-container',
          title: 'player-discussion-comment-deletion-modal-title',
          icon: 'player-discussion-comment-deletion-modal-icon',
          cancelButton: 'player-discussion-comment-deletion-modal-cancel-button',
        },
        callback: this.handleDeletingComment,
      });
    },
    handleDeletingComment() {
      this.isDeletingComment = true;
      this.deleteDiscussionComment({
        interactionId: this.interactionId,
        commentId: this.comment.id,
      }).then((deleted) =>
          this.loadDiscussionComments({ sectionId: this.bulbAttempt.sectionId, interactionId: this.interactionId })
          ).then(() => {
            this.isDeletingComment = false;
            }).catch((err) => {
              this.isDeletingComment = false;
          });
    },
    deleteDummyComment(commentId) {
      this.deleteDummyDiscussionComment({
        interactionId: this.interactionId,
        commentId,
      });
    },
    toggleEdit(val) {
      if (this.isLiveSession && this.interactionMeta.pastSessionTime) return;
      if (typeof val === 'boolean') this.showEdit = val;
      else this.showEdit = !this.showEdit;
      if (this.showEdit) {
        this.showReply = false;
        if (!this.renderEdit) this.renderEdit = true;
        setTimeout(() => {
          EventBus.$emit('setTinymce', {
            key: `discussion-edit-${this.comment.id || 0}`,
            text: this.comment.text,
          });
          EventBus.$emit('focusTinymce', `discussion-edit-${this.comment.id || 0}`);
        }, 25);
      }
    },
    toggleReply(val) {
      if (this.isLiveSession && this.interactionMeta.pastSessionTime) return;
      if (typeof val === 'boolean') this.showReply = val;
      else this.showReply = !this.showReply;
      if (this.showReply) {
        this.showEdit = false;
        if (!this.renderReply) this.renderReply = true;
        setTimeout(() => {
          EventBus.$emit('focusTinymce', `discussion-edit-${this.comment.id || 0}`);
        }, 25);
      }
    },
    setShowReplies(val) {
      this.$emit('setShowReplies', val);
    },
  },
};
</script>

<style lang="scss">
// Common styling for our modals
@import '@mixins/Modal/styles.scss';
</style>